import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { GetFilterUrl, toFixedThree } from '@/utility/helper';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import {
  IproductResponse,
  IproductResponseList,
} from '@/interfaces/productresponse.interface';
import { PageInfo } from '@/interfaces/common.interface';
import { callApi } from '@/api/callApi';
import { useDebounce } from '@/hooks/useDebounce';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import FormField from '../UI/FormField';
import Card from '../UI/Card';
import CommonTable, { TableColumn } from '../common/CommonTable';
import ImagePreview from '../common/media/ImagePreview';
import Pagination from '../common/Pagination';

interface ProductAddOnsProps {
  productId: string;
  selectedAddOnIds: string[];
}

interface ProductState {
  isLoading: boolean;
  products: IproductResponse[];
  pageInfo: PageInfo;
  search: string;
}

export default function ProductAddOns({
  productId,
  selectedAddOnIds,
}: ProductAddOnsProps) {
  const [state, setState] = useState<ProductState>({
    isLoading: false,
    products: [],
    pageInfo: { page: 1, limit: 4, total: 0, totalPages: 0 },
    search: '',
  });

  const debounceSearch = useDebounce(state.search);
  const { setValue } = useFormContext();
  const { selectedItems, handleSelectAll, handleSelectItem, bulkSelectItems } =
    useSelectableTable(state.products);
  const didPreselect = useRef(false);

  const handleStateChange = (newState: Partial<ProductState>) =>
    setState((prev) => ({ ...prev, ...newState }));

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        handleStateChange({ isLoading: true });
        const query = GetFilterUrl({
          page: state.pageInfo.page,
          limit: state.pageInfo.limit,
          search: debounceSearch,
        });

        const res = await callApi<IproductResponseList>(
          `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.products}?${query}`,
          { method: 'GET' },
        );

        handleStateChange({
          isLoading: false,
          products: res.data.filter((p) => String(p.id) !== String(productId)),
          pageInfo: {
            page: +res.pageInfo.page,
            limit: +res.pageInfo.limit,
            total: +res.pageInfo.total,
            totalPages: +res.pageInfo.totalPages,
          },
        });
      } catch (error) {
        console.error('Fetch products error:', error);
        handleStateChange({ isLoading: false });
      }
    };

    fetchProducts();
  }, [debounceSearch, state.pageInfo.page, state.pageInfo.limit]);

  useEffect(() => {
    if (
      didPreselect.current ||
      !state.products.length ||
      !selectedAddOnIds?.length
    )
      return;
    const matched = state.products.filter((p) =>
      selectedAddOnIds.includes(String(p.id)),
    );
    if (matched.length) bulkSelectItems(matched);
    didPreselect.current = true;
  }, [state.products, selectedAddOnIds]);

  useEffect(() => {
    setValue(
      'productInfo.0.meta.addons.product.ids',
      Array.from(selectedItems),
      {
        shouldDirty: false,
        shouldValidate: false,
      },
    );
  }, [selectedItems]);

  const columns: TableColumn<IproductResponse>[] = [
    {
      key: 'product',
      header: 'Product',
      render: (item) => (
        <ImagePreview
          src={`${API_URL_PRODUCT_SERVICE}/${item?.media?.[0]?.url}`}
          alt={item.name}
          text={item.name}
        />
      ),
      className: 'p-3',
    },
    {
      key: 'stock',
      header: 'Stock',
      render: (item) => item.productInfo?.[0]?.quantity || 0,
    },
    {
      key: 'price',
      header: 'Price (BHD)',
      render: (item) => {
        const price =
          item.productInfo?.[0]?.secondaryPrice ||
          item.productInfo?.[0]?.price ||
          0;
        return <span>{toFixedThree(price)}</span>;
      },
    },
  ];

  const handleChangePage = (page: number) => {
    handleStateChange({
      pageInfo: {
        ...state.pageInfo,
        page,
      },
    });
  };

  const handleChangePageSize = (limit: number) => {
    handleStateChange({
      pageInfo: {
        ...state.pageInfo,
        page: 1,
        limit,
      },
    });
  };

  return (
    <Card>
      <h2 className="text-lg font-semibold mb-4">Add On's</h2>

      <FormField
        label="Search Products"
        placeholder="Search For Products"
        value={state.search}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault();
        }}
        onChange={(e) => handleStateChange({ search: e.target.value })}
      />

      <CommonTable
        columns={columns}
        data={state.products}
        showCheckbox
        selectedItems={selectedItems}
        onSelectAll={handleSelectAll}
        onSelectItem={handleSelectItem}
        getItemId={(item) => item.id}
        emptyMessage="No products found"
        loading={state.isLoading}
      />
      <Pagination
        paginationData={state.pageInfo}
        onPageChange={(page) => handleChangePage(page)}
        onPageSizeChange={handleChangePageSize}
      />
    </Card>
  );
}
